import { Alert, Card, Col, ConfigProvider, DatePicker, Divider, Form, Input, Row, Select, Statistic, Typography } from "antd";
import SDIModal from "../../../../components/ui/SDIModal";
import PropTypes from "prop-types";
import frFR from "antd/locale/fr_FR";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInstance from "../../../../variables/api/axiosInstance";
import AvenantAgencySelector from "./AvenantAgencySelector";

const { Text, Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const TransfertDeGaranties = (props) => {
    const [modalTransfert, setModalTransfert] = useState(props.open);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();
    const [professions, setProfessions] = useState([]);

    // États pour les données du contrat et du transfert
    const [contratActuel, setContratActuel] = useState(null);
    const [dateEffetTransfert, setDateEffetTransfert] = useState(dayjs());
    const [fraisAccessoires, setFraisAccessoires] = useState(0); // Frais fixes selon les spécifications
    const [nouveauBeneficiaire, setNouveauBeneficiaire] = useState({
        nom: '',
        prenom: '',
        genre: '',
        dateNaissance: null,
        profession: '',
        adresse: '',
        telephone: '',
        email: ''
    });
    const [carteGrise, setCarteGrise] = useState({
        nouveauNumero: '',
        dateEmission: null,
        organismeEmetteur: ''
    });

    useEffect(() => {
        setModalTransfert(props.open);
        if (props.open && props?.contrat?.codeId) {
            console.log("Contrat pour transfert:", props?.contrat);
            setContratActuel(props?.contrat);
            axiosInstance.get(`/api/avenant/accessoires/TG/${props?.contrat?.engins[0]?.categorie?.codeCategorie}`)
                .then(response => {
                    setFraisAccessoires(response.data);
                })
                .catch(error => {
                    console.error("Erreur lors de la récupération des frais accessoires:", error);
                });
            // La date d'effet par défaut est aujourd'hui
            setDateEffetTransfert(dayjs());

            axiosInstance
                .get(`/api/parametre/type/PROFESSION`)
                .then((res) => {
                    const sorted = [...res.data].sort((a, b) =>
                        (a.libelleParametre || "").localeCompare(
                            b.libelleParametre || "",
                            "fr",
                            { sensitivity: "base" }
                        )
                    );
                    setProfessions(sorted);
                })
                .catch((err) => {
                    console.log(err);
                });
        }
    }, [props.open, props?.contrat?.codeId]);

    const { UserOutlined, CalendarOutlined, DollarOutlined, WarningOutlined } = require('@ant-design/icons');

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XAF' }).format(value);
    };

    const navigate = useNavigate();

    const enterLoading = () => setLoading(true);
    const outLoading = () => setLoading(false);

    const handleTransfertSubmit = async () => {
        try {
            await form.validateFields();

            // Vérifications d'éligibilité
            if (!isContratEligible()) {
                toast.error("Ce contrat n'est pas éligible au transfert de garanties");
                return;
            }

            enterLoading();

            const payload = {
                codeId: contratActuel.codeId,
                dateEffet: dateEffetTransfert.format('YYYY-MM-DDTHH:mm:ss'),
                souscripteur: nouveauBeneficiaire,
                accessoires: fraisAccessoires,
                primeNette: 0,
                taxes: fraisAccessoires * 0.06,
                primeTotale: fraisAccessoires * 1.06
            };

            console.log("Payload transfert:", payload);

            const response = await axiosInstance.put(
                `/api/contrat/transfert-de-garanties`,
                JSON.stringify(payload)
            );

            toast.success("Transfert de garanties effectué avec succès!");
            setModalTransfert(false);
            props.update(false);
            navigate(`/details-contrat/${response.data.codeId}`, { replace: true });
            window.location.reload();

        } catch (error) {
            console.error(error);
            toast.error("Erreur lors du transfert de garanties");
        } finally {
            outLoading();
        }
    };

    const handleCancel = () => {
        setModalTransfert(false);
        props.update(false);
    };

    const isContratEligible = () => {
        return contratActuel?.avenant?.code !== 'SP' &&
            !contratActuel?.sinistre;
    };

    const handleBeneficiaireChange = (field, value) => {
        setNouveauBeneficiaire(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleCarteGriseChange = (field, value) => {
        setCarteGrise(prev => ({
            ...prev,
            [field]: value
        }));
    };

    return (
        <SDIModal
            title="TRANSFERT DE GARANTIES (MUTATION)"
            centered
            confirmLoading={loading}
            open={modalTransfert}
            onOk={handleTransfertSubmit}
            onCancel={handleCancel}
            width={1300}
            zIndex={1940}
            okText="Effectuer le transfert"
            cancelText="Annuler"
        >
            <ConfigProvider locale={frFR}>
                <div className="transfert-content">
                    <AvenantAgencySelector
                        organismeCode={props?.contrat?.organisme?.code}
                        defaultAgence={props?.contrat?.producteur?.agence || props?.contrat?.intermediaire?.agence}
                    />

                    {/* Informations sur le contrat actuel */}
                    {contratActuel && (
                        <Card className="mb-4" size="small" title="Contrat à transférer">
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Statistic
                                        title="Numéro de police"
                                        value={contratActuel.numeroPoliceP || contratActuel.numeroPoliceM}
                                    />
                                </Col>
                                <Col span={8}>
                                    <Statistic
                                        title="Bénéficiaire actuel"
                                        value={`${contratActuel.souscripteur?.nom || 'N/A'} ${contratActuel.souscripteur?.prenom || ''}`}
                                        prefix={<UserOutlined />}
                                    />
                                </Col>
                                <Col span={8}>
                                    <Statistic
                                        title="Date d'échéance"
                                        value={dayjs(contratActuel.dateEcheance).format('DD/MM/YYYY HH:mm:ss')}
                                        prefix={<CalendarOutlined />}
                                    />
                                </Col>
                            </Row>
                        </Card>
                    )}

                    {/* Conditions d'éligibilité */}
                    {!isContratEligible() ? (
                        <Alert
                            message="Contrat non éligible au transfert"
                            description={
                                contratActuel?.avenant?.code === 'SP'
                                    ? "Un contrat suspendu ne peut pas faire l'objet d'un transfert de garanties."
                                    : contratActuel?.sinistre
                                        ? "Un contrat sinistré ne peut pas faire l'objet d'un transfert de garanties."
                                        : "Le contrat doit être en cours de validité pour effectuer un transfert."
                            }
                            type="error"
                            icon={<WarningOutlined />}
                            showIcon
                            className="mb-4"
                        />
                    ) : null}

                    {/* Informations sur le transfert */}
                    {/* <Card className="mb-4" size="small" title="Informations importantes">
                        <div className="info-section">
                            <div className="info-item">
                                <InfoCircleOutlined className="info-icon info" />
                                <Text>
                                    <Text strong>Code avenant :</Text> TG + Année + 001 (premier transfert de l'année)
                                </Text>
                            </div>
                            <div className="info-item">
                                <CarOutlined className="info-icon warning" />
                                <Text>
                                    <Text strong>Justificatif requis :</Text> Nouvelle carte grise avec mutation effectuée
                                </Text>
                            </div>
                            <div className="info-item">
                                <CalendarOutlined className="info-icon success" />
                                <Text>
                                    <Text strong>Échéance :</Text> Les dates d'effet et d'échéance restent inchangées
                                </Text>
                            </div>
                        </div>
                    </Card> */}

                    <Form form={form} layout="vertical">
                        {/* Date d'effet du transfert */}
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Date d'effet du transfert"
                                    name="dateEffetTransfert"
                                    rules={[{ required: true, message: 'Veuillez sélectionner une date' }]}
                                    initialValue={dateEffetTransfert}
                                >
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        format="DD/MM/YYYY"
                                        value={dateEffetTransfert}
                                        onChange={setDateEffetTransfert}
                                        disabledDate={(current) => {
                                            // Ne pas permettre de dates futures au-delà de l'échéance
                                            const dateEcheance = contratActuel ? dayjs(contratActuel.dateEcheance) : dayjs().add(1, 'year');
                                            return current && (
                                                current < dayjs().startOf('day') ||
                                                current > dateEcheance
                                            );
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="Date d'échéance (inchangée)">
                                    <Input
                                        value={contratActuel ? dayjs(contratActuel.dateEcheance).format('DD/MM/YYYY') : ''}
                                        disabled
                                        style={{ backgroundColor: '#f5f5f5' }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        {/* Informations du nouveau bénéficiaire */}
                        <Card className="mb-4" size="small" title="Nouveau bénéficiaire">
                            <Row gutter={16}>
                                <Col span={6}>
                                    <Form.Item
                                        label="Nom"
                                        name="nouveauNom"
                                        rules={[{ required: true, message: 'Le nom est obligatoire' }]}
                                    >
                                        <Input
                                            value={nouveauBeneficiaire.nom}
                                            onChange={(e) => handleBeneficiaireChange('nom', e.target.value)}
                                            placeholder="Nom du nouveau bénéficiaire"
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item
                                        label="Prénom(s)"
                                        name="prenom"
                                        rules={[{ required: true, message: 'Le prénom est obligatoire' }]}
                                    >
                                        <Input
                                            value={nouveauBeneficiaire.prenom}
                                            onChange={(e) => handleBeneficiaireChange('prenom', e.target.value)}
                                            placeholder="Prénom du nouveau bénéficiaire"
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item
                                        label="Genre"
                                        name="genre"
                                        rules={[{ required: true, message: 'Le genre est obligatoire' }]}
                                    >
                                        <Select
                                            id="genre"
                                            className="w-100"
                                            placeholder="Selectionnez le genre"
                                            value={nouveauBeneficiaire.profession}
                                            onChange={(value) => handleBeneficiaireChange('genre', value)}
                                            style={{ width: '100%' }}
                                        >
                                            <Option value="MASCULIN">
                                                MASCULIN
                                            </Option>
                                            <Option value="FEMININ">
                                                FEMININ
                                            </Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item
                                        label="Date de naissance"
                                        name="dateNaissance"
                                    >
                                        <DatePicker
                                            style={{ width: '100%' }}
                                            format="DD/MM/YYYY"
                                            value={nouveauBeneficiaire.dateNaissance}
                                            onChange={(date) => handleBeneficiaireChange('dateNaissance', date)}
                                            placeholder="Date de naissance"
                                            disabledDate={(current) => current && current > dayjs().subtract(18, 'year')}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col span={6}>
                                    <Form.Item
                                        label="Profession"
                                        name="profession"
                                    >
                                        <Select
                                            id="profession"
                                            className="w-100"
                                            showSearch
                                            placeholder="Selectionnez la profession"
                                            optionFilterProp="children"
                                            value={nouveauBeneficiaire.profession}
                                            onChange={(value) => handleBeneficiaireChange('profession', value)}
                                            filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                                            style={{ width: '100%' }}
                                        >
                                            {professions && professions?.map((prof, id) => (<Option key={id}
                                                value={prof.libelleParametre}>
                                                {prof.libelleParametre}
                                            </Option>))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item
                                        label="Téléphone"
                                        name="telephone"
                                        rules={[{ required: true, message: 'Le téléphone est obligatoire' }]}
                                    >
                                        <Input
                                            value={nouveauBeneficiaire.telephone}
                                            onChange={(e) => handleBeneficiaireChange('telephone', e.target.value)}
                                            placeholder="Numéro de téléphone"
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item
                                        label="Email"
                                        name="email"
                                    >
                                        <Input
                                            value={nouveauBeneficiaire.email}
                                            onChange={(e) => handleBeneficiaireChange('email', e.target.value)}
                                            placeholder="Adresse email (optionnel)"
                                            type="email"
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item
                                        label="Adresse"
                                        name="adresse"
                                        rules={[{ required: true, message: 'L\'adresse est obligatoire' }]}
                                    >
                                        <TextArea
                                            value={nouveauBeneficiaire.adresse}
                                            onChange={(e) => handleBeneficiaireChange('adresse', e.target.value)}
                                            placeholder="Adresse complète"
                                            rows={1}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* Informations de la nouvelle carte grise */}
                        {/* <Card className="mb-4" size="small" title="Nouvelle carte grise">
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item
                                        label="Nouveau numéro de carte grise"
                                        name="nouveauNumeroCarteGrise"
                                        rules={[{ required: true, message: 'Le nouveau numéro est obligatoire' }]}
                                    >
                                        <Input
                                            value={carteGrise.nouveauNumero}
                                            onChange={(e) => handleCarteGriseChange('nouveauNumero', e.target.value)}
                                            placeholder="Numéro de la nouvelle carte grise"
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item
                                        label="Date d'émission"
                                        name="dateEmissionCarteGrise"
                                        rules={[{ required: true, message: 'La date d\'émission est obligatoire' }]}
                                    >
                                        <DatePicker
                                            style={{ width: '100%' }}
                                            format="DD/MM/YYYY"
                                            value={carteGrise.dateEmission}
                                            onChange={(date) => handleCarteGriseChange('dateEmission', date)}
                                            placeholder="Date d'émission"
                                            disabledDate={(current) => current && current > dayjs()}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item
                                        label="Organisme émetteur"
                                        name="organismeEmetteur"
                                        rules={[{ required: true, message: 'L\'organisme émetteur est obligatoire' }]}
                                    >
                                        <Input
                                            value={carteGrise.organismeEmetteur}
                                            onChange={(e) => handleCarteGriseChange('organismeEmetteur', e.target.value)}
                                            placeholder="Organisme émetteur"
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card> */}

                        {/* Récapitulatif financier */}
                        <Card className="cost-summary" size="small">
                            <Title level={5}>
                                <DollarOutlined className="mr-2" />
                                Récapitulatif financier du transfert
                            </Title>
                            <div className="cost-breakdown">
                                <Row justify="space-between" className="cost-line">
                                    <Col>
                                        <Text>Prime d'assurance:</Text>
                                    </Col>
                                    <Col>
                                        <Text strong className="text-green-600">
                                            {formatCurrency(0)} (Aucune prime générée)
                                        </Text>
                                    </Col>
                                </Row>

                                <Row justify="space-between" className="cost-line">
                                    <Col>
                                        <Text>Ristourne:</Text>
                                    </Col>
                                    <Col>
                                        <Text strong className="text-green-600">
                                            {formatCurrency(0)} (Aucune ristourne)
                                        </Text>
                                    </Col>
                                </Row>

                                <Row justify="space-between" className="cost-line">
                                    <Col>
                                        <Text>Frais accessoires:</Text>
                                    </Col>
                                    <Col>
                                        <Text strong>{formatCurrency(fraisAccessoires)}</Text>
                                    </Col>
                                </Row>
                                <Row justify="space-between" className="cost-line">
                                    <Col>
                                        <Text>Taxes:</Text>
                                    </Col>
                                    <Col>
                                        <Text strong>{formatCurrency(fraisAccessoires * 0.06)}</Text>
                                    </Col>
                                </Row>
                                <Divider className="my-2" />

                                <Row justify="space-between" className="cost-line total-cost">
                                    <Col>
                                        <Text strong className="text-lg">MONTANT TOTAL À PAYER:</Text>
                                    </Col>
                                    <Col>
                                        <Text strong className="text-lg text-blue-600">
                                            {formatCurrency(fraisAccessoires * 1.06)}
                                        </Text>
                                    </Col>
                                </Row>
                            </div>
                        </Card>
                    </Form>
                </div>
            </ConfigProvider>

            <style jsx>
                {`
                    .transfert-content {
                        max-height: 70vh;
                        overflow-y: auto;
                    }
                    
                    .info-section {
                        display: flex;
                        flex-direction: column;
                        gap: 12px;
                    }
                    
                    .info-item {
                        display: flex;
                        align-items: flex-start;
                        gap: 8px;
                    }
                    
                    .info-icon {
                        margin-top: 2px;
                        font-size: 16px;
                    }
                    
                    .info-icon.warning {
                        color: #faad14;
                    }
                    
                    .info-icon.info {
                        color: #1890ff;
                    }
                    
                    .info-icon.success {
                        color: #52c41a;
                    }
                    
                    .cost-summary {
                        background-color: #f8f9fa;
                        border: 1px solid #e9ecef;
                    }
                    
                    .cost-breakdown .cost-line {
                        margin-bottom: 8px;
                    }
                    
                    .cost-breakdown .total-cost {
                        background-color: #e6f7ff;
                        padding: 8px;
                        border-radius: 4px;
                        margin-top: 8px;
                    }
                    
                    .mr-2 { margin-right: 8px; }
                    .mb-4 { margin-bottom: 16px; }
                    .my-2 { margin: 8px 0; }
                    .text-lg { font-size: 16px; }
                    .text-blue-600 { color: #1890ff; }
                    .text-green-600 { color: #52c41a; }
                `}
            </style>
        </SDIModal>
    );
};

TransfertDeGaranties.propTypes = {
    open: PropTypes.bool,
    update: PropTypes.func,
    contrat: PropTypes.object
};

export default TransfertDeGaranties;
